// Calendar arithmetic on plain yyyy-mm-dd strings, in UTC so no time zone can shift a day.
// The engine works in days (PRD §3.1); everything above it is built from these.

export type Iso = string; // yyyy-mm-dd
export type Month = string; // yyyy-mm

const DAY_MS = 86_400_000;

export const toMs = (day: Iso): number => Date.parse(`${day}T00:00:00Z`);
export const fromMs = (ms: number): Iso => new Date(ms).toISOString().slice(0, 10);
export const addDays = (day: Iso, n: number): Iso => fromMs(toMs(day) + n * DAY_MS);
export const daysBetween = (from: Iso, to: Iso): number => Math.round((toMs(to) - toMs(from)) / DAY_MS);

/** 0 = Sunday … 6 = Saturday. */
export const dayOfWeek = (day: Iso): number => new Date(toMs(day)).getUTCDay();
export const isWeekday = (day: Iso): boolean => {
  const d = dayOfWeek(day);
  return d >= 1 && d <= 5;
};

/** Weeks start on Monday, as the Timecard Tool's Power Query does. */
export const mondayOf = (day: Iso): Iso => {
  const d = dayOfWeek(day);
  return addDays(day, d === 0 ? -6 : 1 - d);
};

export const monthOf = (day: Iso): Month => day.slice(0, 7);

export function daysInMonth(month: Month): number {
  const [y, m] = month.split("-").map(Number);
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

export const firstDayOf = (month: Month): Iso => `${month}-01`;
export const lastDayOf = (month: Month): Iso => `${month}-${String(daysInMonth(month)).padStart(2, "0")}`;

export function addMonths(month: Month, n: number): Month {
  const [y, m] = month.split("-").map(Number);
  const index = y * 12 + (m - 1) + n;
  return `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, "0")}`;
}

/** Every day from `from` to `to`, both inclusive. */
export function* eachDay(from: Iso, to: Iso): Generator<Iso> {
  for (let day = from; day <= to; day = addDays(day, 1)) yield day;
}

/** Every month touched by the range, both ends inclusive. */
export function* eachMonth(from: Iso, to: Iso): Generator<Month> {
  for (let month = monthOf(from); month <= monthOf(to); month = addMonths(month, 1)) yield month;
}

export const yearOf = (day: Iso): number => Number(day.slice(0, 4));

export const daysInYear = (year: number): number => daysBetween(`${String(year).padStart(4, "0")}-01-01`, `${String(year + 1).padStart(4, "0")}-01-01`);

/** Today's date in UTC. */
export const today = (): Iso => new Date().toISOString().slice(0, 10);

/** The years the app's data can carry. A date picker turns a typed "26" into 0026; that is refused. */
export const MIN_YEAR = 2000;
export const MAX_YEAR = 2099;

export const isIsoDate = (value: unknown): value is Iso =>
  typeof value === "string" &&
  /^\d{4}-\d{2}-\d{2}$/.test(value) &&
  Number.isFinite(toMs(value)) && // a month or day out of range parses as NaN, which fromMs can't format
  fromMs(toMs(value)) === value &&
  yearOf(value) >= MIN_YEAR &&
  yearOf(value) <= MAX_YEAR;

/** A date outside the years the app can carry: almost always a mistyped year on an old record. */
export const isImplausibleDate = (value: unknown): boolean => typeof value === "string" && value !== "" && !isIsoDate(value);

const yearText = (year: number) => String(year).padStart(4, "0");

export function weekdaysInYear(year: number): number {
  if (!Number.isInteger(year) || year < 1 || year > 9999) return 0;
  let count = 0;
  for (const day of eachDay(`${yearText(year)}-01-01`, `${yearText(year)}-12-31`)) if (isWeekday(day)) count++;
  return count;
}
